<template>
    <div ref="boxRef"
         class="c-scrollbox"
         :class="[$style.VScrollBox, { [$style[`_${theme}`]]: theme }]"
         :style="{ width: width ? width : undefined, height: height ? height : undefined }"
    >
        <div ref="placeholderRef"
             :class="$style.placeholder"
        />

        <div :class="[$style.fadeMask, classListMask]">
            <div ref="wrapperRef"
                 data-scroll-lock-scrollable
                 class="scrollbox-wrapper"
                 :class="[$style.wrapper, { [$style._offset]: isYOverflowing && !skipOffset }]"
                 @scroll="onScroll"
            >
                <div ref="contentRef"
                     :class="[$style.content, { [$style._full]: contentFullHeight }]"
                >
                    <slot />
                </div>
            </div>
        </div>

        <div v-if="isYOverflowing"
             ref="yRailRef"
             :class="[$style.scrollbar, $style._vertical]"
             @mousedown="onRailClick($event, 'y')"
        >
            <div ref="yThumbRef"
                 :class="$style.thumb"
                 :style="{ height: `${dimensions.yThumbHeight}px`, transform: yScrollPosition }"
                 @mousedown="onThumbClick($event, 'y')"
            />
        </div>

        <div v-if="isXOverflowing"
             ref="xRailRef"
             :class="[$style.scrollbar, $style._horizontal]"
             @mousedown="onRailClick($event, 'x')"
        >
            <div ref="xThumbRef"
                 :class="$style.thumb"
                 :style="{ width: `${dimensions.xThumbWidth}px`, transform: xScrollPosition }"
                 @mousedown="onThumbClick($event, 'x')"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    // #region Imports
    import type { VScrollBoxProps } from './types'
    import { getFontSize } from '~/src/shared/utils/ui-utils'
    // #endregion

    // #region Props
    const props = withDefaults(defineProps<VScrollBoxProps>(), {
        resizable: false,
        skipOffset: false,
        fade: false,
        theme: 'default',
    })

    // #endregion

    // #region Emits
    const emit = defineEmits<{
        'on-scroll': []
        'scroll-end': [value: boolean]
    }>()
    const { resizable, skipOffset, fade, theme, width, height, contentFullHeight } = toRefs(props)
    // #endregion

    // #region Data
    const contentRef = ref<HTMLElement | null>(null)
    const wrapperRef = ref<HTMLElement | null>(null)
    const boxRef = ref<HTMLElement | null>(null)
    const yRailRef = ref<HTMLElement | null>(null)
    const yThumbRef = ref<HTMLElement | null>(null)
    const xRailRef = ref<HTMLElement | null>(null)
    const xThumbRef = ref<HTMLElement | null>(null)
    const placeholderRef = ref<HTMLElement | null>(null)

    const scrollXTicking = ref(false)
    const scrollYTicking = ref(false)
    const isYOverflowing = ref(false)
    const isXOverflowing = ref(false)
    const draggingAxis = ref<'x' | 'y'>('y')
    const resizeObserverContent = ref<ResizeObserver | null>(null)

    const axis = reactive({
        x: {
            clientAttr: 'clientX',
            offsetAttr: 'left',
            offsetSizeAttr: 'offsetWidth',
            scrollSizeAttr: 'scrollWidth',
            scrollOffsetAttr: 'scrollLeft',
            scrollbarSize: 0,
            scrollLeft: 0,
            click: 0,
        },
        y: {
            clientAttr: 'clientY',
            offsetAttr: 'top',
            offsetSizeAttr: 'offsetHeight',
            scrollSizeAttr: 'scrollHeight',
            scrollOffsetAttr: 'scrollTop',
            scrollbarSize: 0,
            scrollTop: 0,
            click: 0,
        },
    })

    const dimensions = reactive({
        boxHeight: 0,
        boxWidth: 0,
        contentHeight: 0,
        contentWidth: 0,
        yRailHeight: 0,
        yThumbHeight: 0,
        xRailWidth: 0,
        xThumbWidth: 0,
    })
    // #endregion

    // #region Lifecycle
    onMounted(() => {
        if (resizable.value && contentRef.value) {
            resizeObserverContent.value = new ResizeObserver(onUpdate)
            resizeObserverContent.value.observe(contentRef.value)
        } else {
            nextTick(() => {
                onUpdate()
            })
        }
    })

    onBeforeUnmount(() => {
        if (resizable.value && resizeObserverContent.value && contentRef.value) {
            resizeObserverContent.value.disconnect()
            resizeObserverContent.value = null
        }
    })
    // #endregion

    // methods
    async function onUpdate() {
        const contentHeight = contentRef.value?.scrollHeight || 0
        const contentWidth = contentRef.value?.scrollWidth || 0
        const placeholder = placeholderRef.value as HTMLElement

        // Determine placeholder size
        if (resizable.value) {
            const surplus = 5.6 * getFontSize()
            placeholder.style.width = `${Math.round(contentWidth + surplus)}px`
        }

        placeholder.style.height = `${contentHeight}px`

        const boxWidth = boxRef.value?.offsetWidth || 0
        const boxHeight = boxRef.value?.offsetHeight || 0

        isXOverflowing.value = contentWidth > boxWidth
        isYOverflowing.value = contentHeight > boxHeight

        nextTick(() => {
            dimensions.boxHeight = boxHeight
            dimensions.boxWidth = boxWidth
            dimensions.contentHeight = contentHeight
            dimensions.contentWidth = contentWidth

            if (boxHeight >= contentHeight) {
                emit('scroll-end', true)
            } else {
                emit('scroll-end', false)
            }

            if (isYOverflowing.value && yRailRef.value) {
                dimensions.yRailHeight = yRailRef.value.offsetHeight
                dimensions.yThumbHeight = handleGetScrollbarSize('y')
            }

            if (isXOverflowing.value && xRailRef.value) {
                dimensions.xRailWidth = xRailRef.value.offsetWidth
                dimensions.xThumbWidth = handleGetScrollbarSize('x')
            }
        })
    }

    function onScroll() {
        if (!scrollXTicking.value && isXOverflowing.value) {
            requestAnimationFrame(() => {
                axis.x.scrollLeft = wrapperRef?.value?.scrollLeft || 0
                scrollXTicking.value = false
            })
            scrollXTicking.value = true
        }

        if (!scrollYTicking.value && isYOverflowing.value) {
            requestAnimationFrame(() => {
                axis.y.scrollTop = wrapperRef?.value?.scrollTop || 0
                scrollYTicking.value = false
            })
            scrollYTicking.value = true
        }
        emit('on-scroll')
    }

    function onThumbClick(e: MouseEvent, targetAxis: 'x' | 'y') {
        if (e.ctrlKey || e.button === 2) {
            return
        }
        e.stopImmediatePropagation()

        const currentTarget = e.currentTarget as HTMLElement
        const currentAxisTarget = axis[targetAxis as keyof typeof axis]

        currentAxisTarget.click =
            currentTarget[currentAxisTarget.offsetSizeAttr] -
            (e[currentAxisTarget.clientAttr] - currentTarget.getBoundingClientRect()[currentAxisTarget.offsetAttr])

        draggingAxis.value = targetAxis
        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
        document.onselectstart = () => false
    }

    function onMouseMove(e: MouseEvent) {
        const prevPage = axis[draggingAxis.value].click
        if (!prevPage || !wrapperRef.value) {
            return
        }

        const rail = draggingAxis.value === 'y' ? (yRailRef.value as HTMLElement) : (xRailRef.value as HTMLElement)
        const thumb = draggingAxis.value === 'y' ? (yThumbRef.value as HTMLElement) : (xThumbRef.value as HTMLElement)

        const offset =
            (rail.getBoundingClientRect()[axis[draggingAxis.value].offsetAttr] -
                e[axis[draggingAxis.value].clientAttr]) *
            -1
        const thumbClickPosition = thumb[axis[draggingAxis.value].offsetSizeAttr] - prevPage
        const thumbPositionPercentage =
            ((offset - thumbClickPosition) * 100) / rail[axis[draggingAxis.value].offsetSizeAttr]

        wrapperRef.value[axis[draggingAxis.value].scrollOffsetAttr] =
            (thumbPositionPercentage * wrapperRef.value[axis[draggingAxis.value].scrollSizeAttr]) / 100
    }

    function onMouseUp(e: MouseEvent) {
        e.preventDefault()
        axis[draggingAxis.value].click = 0
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
        document.onselectstart = null
    }

    function onRailClick(e: MouseEvent, targetAxis: 'x' | 'y') {
        if (!wrapperRef.value) {
            return
        }

        const offset = Math.abs(
            (e.target as HTMLInputElement).getBoundingClientRect()[axis[targetAxis as keyof typeof axis].offsetAttr] -
                e[axis[targetAxis as keyof typeof axis].clientAttr]
        )
        const rail = targetAxis === 'y' ? (yRailRef.value as HTMLElement) : (xRailRef.value as HTMLElement)
        const thumb = targetAxis === 'y' ? (yThumbRef.value as HTMLElement) : (xThumbRef.value as HTMLElement)
        const thumbHalf = thumb[axis[targetAxis as keyof typeof axis].offsetSizeAttr] / 2
        const thumbPositionPercentage =
            ((offset - thumbHalf) * 100) / rail[axis[targetAxis as keyof typeof axis].offsetSizeAttr]

        wrapperRef.value[axis[targetAxis as keyof typeof axis].scrollOffsetAttr] =
            (thumbPositionPercentage * wrapperRef.value[axis[targetAxis as keyof typeof axis].scrollSizeAttr]) / 100
    }

    function handleGetScrollbarSize(targetAxis: 'x' | 'y') {
        if (contentRef.value && ((targetAxis === 'y' && isYOverflowing) || (targetAxis === 'x' && isXOverflowing))) {
            const contentSize = contentRef.value[axis[targetAxis as keyof typeof axis].scrollSizeAttr]
            const trackSize = (targetAxis === 'y' ? yRailRef.value?.offsetHeight : xRailRef.value?.offsetWidth) || 0
            const scrollbarRatio = trackSize / contentSize
            const scrollbarSize = Math.max(~~(scrollbarRatio * trackSize), 20)

            return scrollbarSize
        } else {
            return 0
        }
    }

    // computed
    const classListMask = computed(() => [
        {
            _top: axis.y.scrollTop / (dimensions.contentHeight - dimensions.boxHeight) <= 0.02,
            _bottom: axis.y.scrollTop / (dimensions.contentHeight - dimensions.boxHeight) >= 0.98,
            _show: fade.value && isYOverflowing.value,
        },
    ])

    const yScrollPosition = computed(() => {
        if (!isYOverflowing.value) {
            return 0
        }

        const scrollPercentage = axis.y.scrollTop / (dimensions.contentHeight - dimensions.boxHeight)

        const handleOffset = ~~((dimensions.yRailHeight - dimensions.yThumbHeight) * scrollPercentage)

        return `translate3d(0, ${handleOffset}px, 0)`
    })

    const xScrollPosition = computed(() => {
        if (!isXOverflowing.value) {
            return 0
        }

        const scrollPercentage = axis.x.scrollLeft / (dimensions.contentWidth - dimensions.boxWidth)

        const handleOffset = ~~((dimensions.xRailWidth - dimensions.xThumbWidth) * scrollPercentage)

        return `translate3d(${handleOffset}px, 0, 0)`
    })
</script>

<style lang="scss" module>
    $base-0: #000;
    $primary-default: #7a7a7a;
    $thumb-dark-color: var(--grey-200);
    $thumb-light-color: var(--grey-200);
    $scrollbar-color: var(--grey-100);

    .VScrollBox {
        position: relative;
        overflow: hidden;

        /* Colors */
        &._dark {
            color: $primary-default;

            .thumb {
                background-color: $thumb-dark-color;
            }
        }

        &._light {
            color: $base-0;

            .thumb {
                background-color: $thumb-light-color;
            }
        }

        &._default {
            .scrollbar {
                background-color: var(--grey-50);
            }

            .thumb {
                background-color: var(--grey-200);
            }
        }

        .wrapper {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: 0;
            box-sizing: border-box;
            overflow: auto;
            width: auto;
            max-width: 100%;
            height: 100%;
            max-height: 100%;
            scrollbar-width: none;
            -ms-overflow-style: none;

            &::-webkit-scrollbar {
                width: 0;
                height: 0;
            }

            &._offset {
                padding-right: 0.4rem;
            }
        }

        .placeholder {
            width: 100%;
            max-width: 100%;
            max-height: 100%;
            pointer-events: none;
        }

        .fadeMask {
            position: absolute;
            top: 0;
            width: 100%;
            height: 100%;

            &:global {
                &._top {
                    &:before {
                        min-height: 0;
                    }
                }

                &._bottom {
                    &:after {
                        min-height: 0;
                    }
                }

                &._show {
                    &:before,
                    &:after {
                        display: block;
                    }
                }
            }

            &:before,
            &:after {
                content: '';
                position: absolute;
                left: 1px;
                z-index: 2;
                display: none;
                width: calc(100% - 1.2rem);
                min-height: 4rem;
                transition: min-height 0.2s linear;
                pointer-events: none;
            }

            &:before {
                top: 0;
                background: linear-gradient(
                    to bottom,
                    rgba(255 255 255) 12%,
                    rgba(255 255 255 / 80%) 50%,
                    rgba(255 255 255 / 0%)
                );
            }

            &:after {
                bottom: 0;
                background: linear-gradient(
                    to top,
                    rgba(255 255 255) 12%,
                    rgba(255 255 255 / 80%) 50%,
                    rgba(255 255 255 / 0%)
                );
            }
        }
    }

    .scrollbar {
        position: absolute;
        right: 0;
        bottom: 0;
        z-index: 3;
        overflow: hidden;
        border-radius: 6rem;
        background-color: $scrollbar-color;

        &._horizontal {
            bottom: 0;
            left: 0;
            height: 0.4rem;

            .thumb {
                bottom: 0.1rem;
                left: 0;
                height: 0.4rem;
            }
        }

        &._vertical {
            top: 0;
            width: 0.4rem;

            .thumb {
                top: 0;
                right: 0;
                width: 0.4rem;
                transition: opacity $default-transition;

                &:hover {
                    opacity: 0.7;
                }
            }
        }

        .thumb {
            position: absolute;
            border-radius: 6rem;
            cursor: pointer;
        }
    }

    .content {
        &._full {
            height: 100%;
        }
    }
</style>
